import { useEffect, useMemo, useRef } from "react";
import { Button } from "primereact/button";

import { CardTemplate } from "./CardTemplate";
import { useIntroHighlightedContent } from "../../../../featureFlags";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { useSegmentTrackEvent } from "sj-ui-toolkit/src/hooks";
import { hasAiBundle } from "sj-ui-toolkit/src/constants";

// LD provides the image filename, which we match to the corresponding image in our public/images folder
const addImageUrls = items =>
  items.map(item => (item.image ? { ...item, image: `images/${item.image}.png` } : item));

export const HighlightedContent = ({ additionalItems = [] }) => {
  const { isTourVisible, toggleTourVisibility, contentBundles } = useUserContext();
  const flagCardValues = useIntroHighlightedContent();
  const trackEvent = useSegmentTrackEvent();
  const hasSentTourViewed = useRef(false);

  // Derive cards from the latest bundles. While loading (contentBundles === null) we show no tour
  // cards rather than risk showing the wrong set (e.g. enterprise cards to an AI tenant).
  // On error the lookup yields empty bundles, so we fall back to the default (non-AI) cards.
  const tourItems = useMemo(() => {
    if (!flagCardValues || contentBundles === null) return [];
    const bundles = Array.isArray(contentBundles) ? contentBundles : [];
    const sourceItems = hasAiBundle(bundles)
      ? (flagCardValues.aiOnlyItems ?? []).filter(item =>
          (item.showIf ?? []).some(b => bundles.includes(b))
        )
      : (flagCardValues.defaultItems ?? []);
    return addImageUrls(sourceItems);
  }, [flagCardValues, contentBundles]);

  const highlightedCardData = useMemo(() => {
    return isTourVisible ? [...tourItems, ...additionalItems] : [...additionalItems];
  }, [additionalItems, tourItems, isTourVisible]);

  useEffect(() => {
    if (isTourVisible && highlightedCardData.length && !hasSentTourViewed.current) {
      trackEvent("Tour viewed", {});
      hasSentTourViewed.current = true;
    }
  }, [isTourVisible, highlightedCardData, trackEvent]);

  if (!highlightedCardData.length || !isTourVisible) {
    return null;
  }
  return (
    <div className="pb-5">
      <div className="flex justify-end pb-1">
        <Button
          label="Dismiss"
          icon="pi pi-times"
          severity="secondary"
          text={true}
          onClick={toggleTourVisibility}
        />
      </div>
      <div className="horizontalCardScrollContainer">
        {highlightedCardData.map((item, index) => (
          <CardTemplate {...item} key={item.id || index} />
        ))}
      </div>
    </div>
  );
};
